import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.ts';
import {
  ErrorNotice, Loading, Field, TextInput, Select, StateSelect, DateInput,
} from '../components/Bits.tsx';
import { useApplication } from './Dashboard.tsx';

const FIELDS = [
  'first_name', 'middle_name', 'last_name', 'address', 'city', 'state', 'zip',
  'telephone', 'dob', 'gender',
] as const;

type Form = Record<(typeof FIELDS)[number], string>;

export function BasicInfo() {
  const { data, isPending, error } = useApplication();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form>(
    () => Object.fromEntries(FIELDS.map((f) => [f, ''])) as Form,
  );

  useEffect(() => {
    if (!data) return;
    const a = data.applicant;
    setForm({
      first_name: a.firstName ?? '', middle_name: a.middleName ?? '', last_name: a.lastName ?? '',
      address: a.address ?? '', city: a.city ?? '', state: a.state ?? '', zip: a.zip ?? '',
      telephone: a.telephone ?? '', dob: a.dob ?? '', gender: a.gender ?? '',
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.put('/api/application/basicinfo', form),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['application'] });
      navigate('/apply');
    },
  });

  if (isPending) return <main className="narrow" id="main"><Loading /></main>;
  if (error) return <main className="narrow" id="main"><ErrorNotice error={error} /></main>;

  const set = (key: keyof Form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value });

  return (
    <main className="narrow" id="main">
      <p><Link to="/apply">← Back to your application</Link></p>
      <h2>Basic information</h2>
      <p className="lede">How we reach you, and how your name should appear.</p>

      <ErrorNotice error={save.error} />

      <form className="panel" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="row">
          <Field label="First name *"><TextInput value={form.first_name} required onChange={set('first_name')} /></Field>
          <Field label="Middle name"><TextInput value={form.middle_name} onChange={set('middle_name')} /></Field>
        </div>
        <Field label="Last name *"><TextInput value={form.last_name} required onChange={set('last_name')} /></Field>
        <Field label="Street address *"><TextInput value={form.address} required onChange={set('address')} /></Field>
        <div className="row">
          <Field label="City *"><TextInput value={form.city} required onChange={set('city')} /></Field>
          <Field label="State *">
            <StateSelect value={form.state} required onChange={set('state')} />
          </Field>
          <Field label="ZIP code *"><TextInput value={form.zip} required pattern="\d{5}" onChange={set('zip')} /></Field>
        </div>
        {/* The legacy app rejected "555-123-4567" outright with "Do not use any hyphens".
            Punctuation is now stripped server-side. */}
        <Field label="Telephone *" hint="Ten digits. Dashes and brackets are fine.">
          <TextInput value={form.telephone} required onChange={set('telephone')} />
        </Field>
        <div className="row">
          <Field label="Date of birth" hint="Pick from the calendar, or type MM/DD/YYYY.">
            <DateInput
              value={form.dob}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(dob) => setForm((f) => ({ ...f, dob }))}
            />
          </Field>
          <Field label="Gender">
            <Select value={form.gender} onChange={set('gender')}>
              <option value="">Prefer not to say</option>
              <option value="F">Female</option>
              <option value="M">Male</option>
              <option value="X">Another term</option>
            </Select>
          </Field>
        </div>

        <div className="actions">
          <button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
          <button type="button" className="secondary" onClick={() => navigate('/apply')}>Cancel</button>
        </div>
      </form>
    </main>
  );
}
